import { Accordion } from "@ab-inbev-maz/compass";
import { memo, useCallback, useImperativeHandle, useReducer, forwardRef, type ForwardedRef } from "react";

const accordionStyle = {
    padding: '0 10px',
} as React.CSSProperties;

interface AccordionItem {
    title: string;
    Component: () => React.ReactNode;
}

interface AccordionGroupProps {
    items: AccordionItem[];
}

export interface AccordionGroupRef {
    disable: () => void;
    next: () => void;
    prev: () => void;
}

const getAccordionClassName = (enabled: number, accordionIndex: number) => {
    if (enabled > accordionIndex) return 'pt-3 border !border-b rounded-xl !border-green-500';
    if (enabled >= accordionIndex) return 'pt-3 border !border-b rounded-xl !border-black';
    return 'pt-3 border !border-b rounded-xl !border-black opacity-50 pointer-events-none';
};

const getAccordionStatus = (enabled: number, index: number) => {
    if (index > enabled) return 'disabled';
    if (index === enabled) return 'idle';
    return 'success';
};

const AccordionGroup = forwardRef(({ items }: AccordionGroupProps, ref: ForwardedRef<AccordionGroupRef>) => {
    const [accordion, updateAccordion] = useReducer(
        (prev, next) => ({ ...prev, ...next }), {
        current: 1,
        enabled: 1
    });
    const changeAccordion = (accordionIndex: number) => {
        updateAccordion({ current: accordion.current === accordionIndex ? 0 : accordionIndex });
    };

    const restAccordion = useCallback((accordionIndex: number) => {
        return {
            open: accordion.current === accordionIndex,
            onChange: changeAccordion.bind(null, accordionIndex),
            className: getAccordionClassName(accordion.enabled, accordionIndex),
        };
    }, [accordion.current, accordion.enabled]);

    const status = useCallback((index: number) => {
        return getAccordionStatus(accordion.enabled, index);
    }, [accordion.enabled]);

    useImperativeHandle(ref, () => ({
        next: () => {
            const newCurrent = accordion.current + 1;
            updateAccordion({ current: newCurrent, enabled: newCurrent });
        },
        prev: () => {
            const newCurrent = Math.max(accordion.current - 1, 1);
            updateAccordion({ current: newCurrent, enabled: newCurrent });
        },
        disable: () => {
            const newEnabled = Math.max(accordion.enabled - 1, 1);
            updateAccordion({ enabled: newEnabled });
        }
    }));

    return (
        <div className="flex flex-col gap-4">
            {
                items.map(({ Component, ...rest }, index) => (
                    <Accordion
                        {...restAccordion(index + 1)}
                        key={rest.title}
                        title={<AccordionTitle number={index + 1} status={status(index + 1)} {...rest} />}
                        status="warning"
                        style={accordionStyle}
                    >
                        <Component />
                    </Accordion>
                ))
            }
        </div>
    )
})

interface AccordionTitleProps {
    title: string;
    number: number;
    status: 'success' | 'disabled' | 'idle';
    description?: string;
}

const AccordionTitle = memo(({ title, number, status, description }: AccordionTitleProps) => {
    const statusColor = {
        success: 'bg-[#29D36B] text-[#FFFFFF]',
        disabled: 'bg-[#202020] text-[#FFFFFF]',
        idle: 'bg-[#FFCC00] text-[#000000]',
    }

    return (
        <div className='flex items-center gap-4 h-[44px]'>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold text-l ${statusColor[status]}`}>{number}</div>
            <div className="grid">
                <p className='text-lg font-semibold'>{title}</p>
                <p className='text-xs'>{description}</p>
            </div>
        </div >
    )
})

export default AccordionGroup;